import { useState, useEffect, useMemo, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Search, 
  X, 
  Check, 
  ChevronsUpDown, 
  User,
  Filter,
  Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { AssignmentType, AssignmentStatus } from "@/types/assignments";

export interface FilterState {
  type?: AssignmentType;
  userId?: string;
  status?: AssignmentStatus;
  search?: string;
}

interface AssignmentFiltersProps {
  onFilterChange: (filters: FilterState) => void;
  initialStatus?: AssignmentStatus;
}

interface UserOption {
  id: string;
  full_name: string;
  avatar_url?: string;
}

export function AssignmentFilters({ onFilterChange, initialStatus }: AssignmentFiltersProps) {
  const [type, setType] = useState<string>("all");
  const [userId, setUserId] = useState<string>("");
  const [status, setStatus] = useState<string>(initialStatus || "all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [userPopoverOpen, setUserPopoverOpen] = useState(false);

  // Fetch users for combobox
  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ["users-for-assignment-filter"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .order("full_name");
      
      if (error) throw error;
      return (data || []) as UserOption[];
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Debounce search with loading indicator
  useEffect(() => {
    if (search !== debouncedSearch) {
      setIsSearching(true);
    }
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setIsSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  // Build filter state and notify parent
  useEffect(() => {
    const filters: FilterState = {};
    
    if (type !== "all") {
      filters.type = type as AssignmentType;
    }
    if (userId) {
      filters.userId = userId;
    }
    if (status !== "all") {
      filters.status = status as AssignmentStatus;
    }
    if (debouncedSearch.trim()) {
      filters.search = debouncedSearch.trim();
    }
    
    onFilterChange(filters);
  }, [type, userId, status, debouncedSearch, onFilterChange]);

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (type !== "all") count++;
    if (userId) count++;
    if (status !== "all") count++;
    if (search.trim()) count++;
    return count;
  }, [type, userId, status, search]);

  // Clear all filters
  const handleClearAll = useCallback(() => {
    setType("all");
    setUserId("");
    setStatus("all");
    setSearch("");
  }, []);

  // Get selected user name
  const selectedUserName = useMemo(() => {
    if (!userId) return null;
    const user = users.find(u => u.id === userId);
    return user?.full_name || "Unknown user";
  }, [userId, users]);

  const getInitials = (name: string) => {
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <div className="space-y-4" role="search" aria-label="Filter assignments">
      {/* Filter Controls - Responsive Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Type Filter */}
        <div className="space-y-1.5">
          <label 
            htmlFor="type-filter" 
            className="text-sm font-medium text-muted-foreground"
          >
            Type
          </label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger 
              id="type-filter" 
              className="bg-background w-full"
              aria-label="Filter by assignment type"
            >
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="curriculum">Curriculum</SelectItem>
              <SelectItem value="course">Course</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* User Combobox */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-muted-foreground">User</label>
          {usersLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <Popover open={userPopoverOpen} onOpenChange={setUserPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={userPopoverOpen}
                  aria-label="Filter by user"
                  className="w-full justify-between bg-background font-normal"
                >
                  {selectedUserName ? (
                    <span className="truncate">{selectedUserName}</span>
                  ) : (
                    <span className="text-muted-foreground">All users</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0 bg-background z-50" align="start">
                <Command>
                  <CommandInput placeholder="Search users..." />
                  <CommandList>
                    <CommandEmpty>No users found.</CommandEmpty>
                    <CommandGroup>
                      {/* All Users option */}
                      <CommandItem
                        value="all-users"
                        onSelect={() => {
                          setUserId("");
                          setUserPopoverOpen(false);
                        }}
                      >
                        <User className="mr-2 h-4 w-4" />
                        <span>All Users</span>
                        <Check
                          className={cn(
                            "ml-auto h-4 w-4",
                            !userId ? "opacity-100" : "opacity-0"
                          )}
                        />
                      </CommandItem>
                      
                      {/* User list */}
                      {users.map((user) => (
                        <CommandItem
                          key={user.id}
                          value={user.full_name || user.id}
                          onSelect={() => {
                            setUserId(user.id);
                            setUserPopoverOpen(false);
                          }}
                        >
                          <Avatar className="h-6 w-6 mr-2">
                            <AvatarImage src={user.avatar_url} />
                            <AvatarFallback className="text-xs">
                              {getInitials(user.full_name || "?")}
                            </AvatarFallback>
                          </Avatar>
                          <span className="truncate">{user.full_name || "Unnamed"}</span>
                          <Check
                            className={cn(
                              "ml-auto h-4 w-4",
                              userId === user.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>

        {/* Status Filter */}
        <div className="space-y-1.5">
          <label 
            htmlFor="status-filter" 
            className="text-sm font-medium text-muted-foreground"
          >
            Status
          </label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger 
              id="status-filter" 
              className="bg-background w-full"
              aria-label="Filter by status"
            >
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="not_started">Not Started</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Search Input */}
        <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
          <label 
            htmlFor="search-filter" 
            className="text-sm font-medium text-muted-foreground"
          >
            Search
          </label>
          <div className="relative">
            {isSearching ? (
              <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
            ) : (
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            )}
            <Input
              id="search-filter"
              placeholder="Search by user or item..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-10"
              aria-label="Search assignments by user or item name"
            />
            {search && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 hover:bg-muted"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Active Filters Summary */}
      {activeFilterCount > 0 && (
        <div 
          className="flex items-center gap-2 flex-wrap animate-fade-in"
          role="status"
          aria-live="polite"
        >
          <Badge variant="secondary" className="gap-1">
            <Filter className="h-3 w-3" />
            {activeFilterCount} {activeFilterCount === 1 ? "filter" : "filters"} active
          </Badge>
          
          {/* Active filter badges */}
          {type !== "all" && (
            <Badge variant="outline" className="gap-1 transition-all hover:bg-muted">
              Type: {type === "curriculum" ? "Curriculum" : "Course"}
              <button 
                onClick={() => setType("all")} 
                className="ml-1 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded-sm"
                aria-label={`Remove ${type} type filter`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          
          {userId && selectedUserName && (
            <Badge variant="outline" className="gap-1 transition-all hover:bg-muted">
              User: {selectedUserName}
              <button 
                onClick={() => setUserId("")} 
                className="ml-1 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded-sm"
                aria-label={`Remove ${selectedUserName} user filter`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          
          {status !== "all" && (
            <Badge variant="outline" className="gap-1 transition-all hover:bg-muted">
              Status: {status === "not_started" ? "Not Started" : status === "in_progress" ? "In Progress" : "Completed"}
              <button 
                onClick={() => setStatus("all")} 
                className="ml-1 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded-sm"
                aria-label={`Remove ${status} status filter`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          
          {search.trim() && (
            <Badge variant="outline" className="gap-1 transition-all hover:bg-muted">
              Search: "{search}"
              <button 
                onClick={() => setSearch("")} 
                className="ml-1 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded-sm"
                aria-label="Clear search filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleClearAll}
            className="text-muted-foreground hover:text-foreground h-7 transition-colors"
          >
            Clear all
          </Button>
        </div>
      )}
    </div>
  );
}
